import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { 
  Send, 
  MessageCircle, 
  Twitter, 
  Linkedin, 
  Facebook, 
  Mail, 
  Copy, 
  Check, 
  Share2, 
  Clock 
} from "lucide-react";
import { toast } from "react-hot-toast";

const ShareModal = ({ open, onOpenChange, title, url, description }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Link copied");
  };

  const shareOptions = [
    { 
      name: "Telegram", 
      icon: Send, 
      color: "bg-sky-500", 
      href: `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}` 
    },
    { 
      name: "WhatsApp", 
      icon: MessageCircle, 
      color: "bg-green-500", 
      href: `https://wa.me/?text=${encodeURIComponent(title + "\n" + url)}` 
    },
    { 
      name: "Twitter", 
      icon: Twitter, 
      color: "bg-black dark:bg-zinc-800", 
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}` 
    },
    { 
      name: "LinkedIn", 
      icon: Linkedin, 
      color: "bg-blue-600", 
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}` 
    },
    { 
      name: "Facebook", 
      icon: Facebook, 
      color: "bg-blue-700", 
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` 
    },
    { 
      name: "Email", 
      icon: Mail, 
      color: "bg-gray-600", 
      href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}` 
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-lg bg-card/95 backdrop-blur-3xl border-border rounded-[2.5rem] shadow-2xl p-0 overflow-hidden outline-none max-h-[90vh] flex flex-col">
        <div className="relative p-5 sm:p-10 overflow-y-auto custom-scrollbar">
          {/* Header Overlay */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
          
          <DialogHeader className="mb-8">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-4 mx-auto sm:mx-0">
              <Share2 className="w-8 h-8 text-primary" />
            </div>
            <DialogTitle className="text-3xl font-black text-center sm:text-left leading-tight">
              Spread the <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Knowledge</span>.
            </DialogTitle>
            {description && (
              <p className="text-muted-foreground font-medium text-center sm:text-left mt-2">
                {description}
              </p>
            )}
          </DialogHeader>

          {/* Share Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4 mb-8 sm:mb-10">
            {shareOptions.map((opt) => (
              <a
                key={opt.name}
                href={opt.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center justify-center gap-2 sm:gap-3 p-4 sm:p-5 rounded-3xl border border-transparent hover:border-primary/10 hover:bg-primary/5 transition-all duration-300 active:scale-95"
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 ${opt.color} rounded-2xl flex items-center justify-center text-white shadow-lg shadow-black/10 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                  <opt.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-colors">
                  {opt.name}
                </span>
              </a>
            ))}
          </div>

          {/* Quick Copy Link */}
          <div className="space-y-3">
            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 ml-1">Copy direct link</p>
            <div className="relative group/copy">
              <div className="flex items-center gap-3 bg-muted/30 dark:bg-muted/10 p-4 rounded-2xl border border-border/50 transition-all group-hover/copy:border-primary/30 group-hover/copy:bg-muted/40">
                <div className="p-2 bg-background rounded-lg text-muted-foreground">
                  <Clock className="w-3.5 h-3.5" />
                </div>
                <p className="text-[11px] font-mono text-muted-foreground truncate flex-1 font-medium tracking-tighter">
                  {url}
                </p>
                <button 
                  onClick={handleCopyLink}
                  className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-xl bg-primary text-white shadow-lg active:scale-90 transition-all"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          
          <div className="mt-8 text-center sm:text-left">
             <p className="text-[10px] font-bold text-muted-foreground/40 italic">Great things are made to be shared 🚀</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
